/**
 * InvoiceListFilters.tsx
 *
 * Collapsible filter bar for ViewInvoice.
 * Fully controlled — all state lives in useInvoiceList hook.
 */

import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon       from "@mui/icons-material/Clear";
import FilterListIcon  from "@mui/icons-material/FilterList";
import RefreshIcon     from "@mui/icons-material/Refresh";
import SearchIcon      from "@mui/icons-material/Search";
import { InvoiceFilters } from "../Hooks/Sales/useInvoiceList";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "all",  label: "All Statuses" },
  { value: "due",  label: "Due / Pending" },
  { value: "paid", label: "Paid" },
  { value: "",     label: "Draft" },
];

const TYPE_OPTIONS = [
  { value: "all",            label: "All Types" },
  { value: "Original",       label: "Original Invoice" },
  { value: "GSTInvoice",     label: "GST Invoice" },
  { value: "QuickInvoice",   label: "Quick Invoice" },
  { value: "PerformaInvoice",label: "Performa Invoice" },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  filters:          InvoiceFilters;
  hasActiveFilters: boolean;
  showFilters:      boolean;
  onToggleFilters:  () => void;
  onUpdateFilter:   <K extends keyof InvoiceFilters>(key: K, value: InvoiceFilters[K]) => void;
  onClearFilters:   () => void;
  onRefresh:        () => void;
  totalCount:       number;
  filteredCount:    number;
  isMobile:         boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const InvoiceListFilters: React.FC<Props> = ({
  filters,
  hasActiveFilters,
  showFilters,
  onToggleFilters,
  onUpdateFilter,
  onClearFilters,
  onRefresh,
  totalCount,
  filteredCount,
  isMobile,
}) => {
  const activeCount = [
    filters.status      !== "all" && filters.status      !== undefined,
    filters.invoiceType !== "all",
    !!filters.fromDate,
    !!filters.toDate,
  ].filter(Boolean).length;

  return (
    <Box mb={2}>
      {/* ── Top bar: search + filter toggle ── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={1.5}
      >
        {/* Live search */}
        <TextField
          size="small"
          placeholder="Search invoice ID, customer, order no..."
          value={filters.search}
          onChange={(e) => onUpdateFilter("search", e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: filters.search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onUpdateFilter("search", "")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ flex: 1, maxWidth: { sm: 400 } }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Filter toggle */}
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            variant={hasActiveFilters ? "contained" : "outlined"}
            color={hasActiveFilters ? "primary" : "inherit"}
            onClick={onToggleFilters}
            sx={{ whiteSpace: "nowrap" }}
          >
            {isMobile
              ? `Filters${activeCount > 0 ? ` (${activeCount})` : ""}`
              : showFilters
                ? "Hide Filters"
                : `Filters${activeCount > 0 ? ` (${activeCount})` : ""}`}
          </Button>

          {/* Refresh */}
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={onRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Record count */}
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {hasActiveFilters
              ? `${filteredCount} of ${totalCount}`
              : `${totalCount} invoice${totalCount !== 1 ? "s" : ""}`}
          </Typography>
        </Stack>
      </Stack>

      {/* ── Collapsible filter panel ── */}
      <Collapse in={showFilters}>
        <Box
          p={isMobile ? 1.5 : 2}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            mb: 1,
          }}
        >
          <Grid container spacing={1.5} alignItems="flex-end">
            {/* Status */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e: SelectChangeEvent) =>
                    onUpdateFilter("status", e.target.value)
                  }
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Invoice Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Invoice Type</InputLabel>
                <Select
                  value={filters.invoiceType}
                  label="Invoice Type"
                  onChange={(e: SelectChangeEvent) =>
                    onUpdateFilter("invoiceType", e.target.value)
                  }
                >
                  {TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* From Date */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                fullWidth
                value={filters.fromDate}
                onChange={(e) => onUpdateFilter("fromDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: filters.toDate || undefined }}
              />
            </Grid>

            {/* To Date */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="To Date"
                type="date"
                size="small"
                fullWidth
                value={filters.toDate}
                onChange={(e) => onUpdateFilter("toDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filters.fromDate || undefined }}
              />
            </Grid>

            {/* Clear */}
            {hasActiveFilters && (
              <Grid item xs={12} sm={12} md={1}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={onClearFilters}
                  fullWidth
                >
                  Clear
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={0.5} mt={1.5} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                Active:
              </Typography>
              {filters.status !== "all" && (
                <Chip
                  label={`Status: ${STATUS_OPTIONS.find((o) => o.value === filters.status)?.label ?? filters.status}`}
                  size="small"
                  onDelete={() => onUpdateFilter("status", "all")}
                />
              )}
              {filters.invoiceType !== "all" && (
                <Chip
                  label={`Type: ${TYPE_OPTIONS.find((o) => o.value === filters.invoiceType)?.label ?? filters.invoiceType}`}
                  size="small"
                  onDelete={() => onUpdateFilter("invoiceType", "all")}
                />
              )}
              {filters.fromDate && (
                <Chip label={`From: ${filters.fromDate}`} size="small" onDelete={() => onUpdateFilter("fromDate", "")} />
              )}
              {filters.toDate && (
                <Chip label={`To: ${filters.toDate}`}   size="small" onDelete={() => onUpdateFilter("toDate", "")} />
              )}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default InvoiceListFilters;